import { observer } from "mobx-react-lite";
import { Plan } from "../../services/PlansService";
import { defaultSvg } from "../../utils/latlngSVG";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { convertDistance, convertElevation } from "../../utils/UnitsUtils";
import { AppContext } from "../../contexts/AppContext";
import React from "react";
import styled from "@mui/material/styles/styled";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import DirectionsRun from "@mui/icons-material/DirectionsRun";
import DirectionsBike from "@mui/icons-material/DirectionsBike";
import Badge from "@mui/material/Badge";

const PlanBox = styled(Box)(({ theme }) => ({
    outline: `solid ${theme.palette.divider}`,
    outlineWidth: 1,
    cursor: "pointer",
    "&:hover": {
        outline: `solid ${theme.palette.common.black}`,
    },
}));

const PlanImageContainer = styled(Box)(({ theme }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.grey[200],
    position: "relative",
}));

const PlanImage = styled("div")(({ theme }) => ({
    width: theme.spacing(20),
    height: theme.spacing(20),
    display: "flex",
    padding: theme.spacing(1),
    color: theme.palette.common.black,
    backgroundColor: theme.palette.grey[200],
    '& path': {
        strokeWidth: 15,
    },
}));

export const PlanItem: React.FC<{ plan: Plan, onClick: () => void }> = observer(({ plan, onClick }) => {

    const intl = useIntl();

    const theme = useTheme();
    const isUpMD = useMediaQuery(theme.breakpoints.up('md'));

    const { userService, reviewService } = React.useContext(AppContext);

    const unreadCount = userService.user?.id ? reviewService.getUnreadForPlan(plan.id, userService.user.id) : 0;

    const distanceUnits = userService.user?.distanceUnits || "km";
    const elevationUnits = userService.user?.elevationUnits || "m";

    return (
        <Box flexBasis={isUpMD ? "50%" : "100%"} maxWidth={isUpMD ? "50%" : "100%"} paddingTop={4} paddingLeft={4}>
            <Badge badgeContent={unreadCount} color="error" sx={{ width: '100%' }}>
            <PlanBox display="flex" justifyContent="center" flexDirection={isUpMD ? "row" : "column"} padding={4} onClick={onClick} width="100%">
                <PlanImageContainer>
                    <PlanImage dangerouslySetInnerHTML={{ __html: plan.svg || defaultSvg }} />
                    <Box position="absolute" top={8} left={8}>
                        {
                            plan.sport === "bike" ? <DirectionsBike /> : <DirectionsRun />
                        }
                    </Box>
                </PlanImageContainer>

                <Grid container marginX={isUpMD ? 4 : 0} marginTop={isUpMD ? 0 : 4}>
                    <Grid item xs={12}>
                        <Typography variant="h6">{plan.name}</Typography>
                        {reviewService.ownerNames[plan.userId] && (
                            <Typography variant="caption" color="text.secondary">
                                {reviewService.ownerNames[plan.userId]}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={6} marginY={1}>
                        <Typography variant="body2">{intl.formatMessage({ id: "data.distanceTitle" })}</Typography>
                        <Typography variant="body2" fontSize={18}>{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(plan.distance, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
                    </Grid>
                    <Grid item xs={6} marginY={1}>
                        <Typography variant="body2">{intl.formatMessage({ id: "data.dateTitle" })}</Typography>
                        <Typography variant="body2" fontSize={18}>{dayjs(plan.date).locale(intl.locale).format("ll")}</Typography>
                    </Grid>
                    <Grid item xs={6} marginY={1}>
                        <Typography variant="body2">{intl.formatMessage({ id: "data.elevationGainTitle" })}</Typography>
                        <Typography variant="body2" fontSize={18}>{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(plan.elevationGain, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                    </Grid>
                    <Grid item xs={6} marginY={1}>
                        <Typography variant="body2">{intl.formatMessage({ id: "data.elevationLossTitle" })}</Typography>
                        <Typography variant="body2" fontSize={18}>{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(plan.elevationLoss, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
                    </Grid>
                </Grid>
            </PlanBox>
            </Badge>
        </Box>
    );
});